import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { serviceSchema, breadcrumbSchema } from '../../utils/structuredData';
import './ServicePage.css';

const IOSDevelopment = () => {
  const serviceStructuredData = serviceSchema(
    'iOS Development',
    'Build stunning, high-performance iOS applications for iPhone and iPad. We create native iOS apps using Swift and SwiftUI that deliver exceptional user experiences.',
    'Mobile Application Development'
  );

  const breadcrumbStructuredData = breadcrumbSchema([
    { name: 'Home', url: 'https://www.gettechupdate.com' },
    { name: 'Services', url: 'https://www.gettechupdate.com/#services' },
    { name: 'iOS Development', url: 'https://www.gettechupdate.com/services/ios-development' }
  ]);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceStructuredData, breadcrumbStructuredData]
  };

  return (
    <>
      <SEO
        title="iOS Development Services | Tech Update"
        description="Expert iOS app development services. Build stunning, high-performance iOS applications for iPhone and iPad using Swift, SwiftUI, and modern frameworks. Native iOS development, App Store optimization, and more."
        keywords="iOS app development, Swift development, SwiftUI, native iOS apps, App Store optimization, iOS development services, iPhone app development, iPad app development"
        url="https://www.gettechupdate.com/services/ios-development"
        structuredData={combinedSchema}
      />
      <div className="service-page">
      <div className="service-hero">
        <div className="service-hero-content">
          <div className="service-icon-large">
            <i className="fab fa-apple"></i>
          </div>
          <h1>iOS Development</h1>
          <p className="service-hero-subtitle">
            Build stunning, high-performance iOS applications that deliver exceptional user experiences and drive business growth on Apple's ecosystem.
          </p>
        </div>
      </div>

      <div className="service-content">
        <section className="service-section">
          <h2>Why Choose Our iOS Development Services?</h2>
          <p>
            The iOS platform represents a premium market with users who value quality, security, and seamless experiences. 
            Our expert team specializes in creating beautiful, high-performance iOS applications using the latest Apple technologies. 
            We deliver native iOS apps that leverage the full power of iPhone and iPad devices, ensuring your app stands out in the App Store.
          </p>
        </section>

        <section className="service-section">
          <h2>Our iOS Development Expertise</h2>
          <div className="expertise-grid">
            <div className="expertise-item">
              <i className="fas fa-code"></i>
              <h3>Native iOS Development</h3>
              <p>We build native iOS apps using Swift and SwiftUI, ensuring optimal performance and seamless integration with Apple's ecosystem and services.</p>
            </div>
            <div className="expertise-item">
              <i className="fab fa-apple"></i>
              <h3>Human Interface Guidelines</h3>
              <p>Beautiful, intuitive interfaces following Apple's Human Interface Guidelines for consistent and delightful user experiences.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-rocket"></i>
              <h3>Performance Optimization</h3>
              <p>We optimize apps for speed, battery efficiency, and smooth performance across all iOS devices and versions.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-shield-alt"></i>
              <h3>Security & Privacy</h3>
              <p>Implementing robust security measures and privacy features to protect user data and ensure App Store compliance.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-store"></i>
              <h3>App Store Optimization</h3>
              <p>Expert assistance with app store listing, ASO, and deployment to Apple App Store for maximum visibility and downloads.</p>
            </div>
            <div className="expertise-item">
              <i className="fas fa-sync-alt"></i>
              <h3>API Integration</h3>
              <p>Seamless integration with RESTful APIs, Apple services, third-party SDKs, and backend systems for complete functionality.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Technologies We Use</h2>
          <div className="tech-stack">
            <div className="tech-item">
              <i className="fab fa-swift"></i>
              <span>Swift</span>
            </div>
            <div className="tech-item">
              <i className="fab fa-apple"></i>
              <span>SwiftUI</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-mobile-alt"></i>
              <span>UIKit</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-database"></i>
              <span>Core Data</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-cloud"></i>
              <span>CloudKit</span>
            </div>
            <div className="tech-item">
              <i className="fas fa-fire"></i>
              <span>Firebase</span>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Our Development Process</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Discovery & Planning</h3>
              <p>We start by understanding your business goals, target audience, and app requirements. This phase includes market research, competitor analysis, and technical feasibility study for iOS platform.</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>Design & Prototyping</h3>
              <p>Our designers create wireframes and high-fidelity prototypes following Apple's Human Interface Guidelines. We ensure the UI/UX is intuitive, beautiful, and engaging.</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Development</h3>
              <p>Using agile methodology, we develop your iOS app in sprints with regular updates. We follow clean code principles, Swift best practices, and Apple's design patterns.</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Testing & QA</h3>
              <p>Comprehensive testing across multiple iOS devices and versions. We perform unit testing, integration testing, UI testing, and user acceptance testing.</p>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <h3>Deployment</h3>
              <p>We assist with App Store submission, including app signing, TestFlight beta testing, store listing optimization, and compliance with App Store guidelines.</p>
            </div>
            <div className="process-step">
              <div className="step-number">6</div>
              <h3>Maintenance & Support</h3>
              <p>Ongoing support, bug fixes, feature updates, iOS version compatibility, and performance monitoring to ensure your app continues to deliver value.</p>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>Key Features We Implement</h2>
          <div className="features-grid">
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Offline Functionality</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Push Notifications</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>In-App Purchases</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Apple Pay Integration</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Social Media Integration</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Face ID & Touch ID</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>ARKit Integration</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Camera & Photo Library</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Location Services</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>iCloud Sync</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Analytics Integration</span>
            </div>
            <div className="feature-item">
              <i className="fas fa-check-circle"></i>
              <span>Widget Development</span>
            </div>
          </div>
        </section>

        <section className="service-section">
          <h2>App Types We Develop</h2>
          <div className="app-types">
            <div className="app-type-card">
              <i className="fas fa-shopping-cart"></i>
              <h3>E-commerce Apps</h3>
              <p>Feature-rich shopping apps with secure payment processing, Apple Pay integration, inventory management, and order tracking.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-users"></i>
              <h3>Social Media Apps</h3>
              <p>Engaging social platforms with real-time messaging, content sharing, and community features optimized for iOS.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-utensils"></i>
              <h3>Food & Restaurant Apps</h3>
              <p>Food delivery apps with menu browsing, ordering, Apple Pay, and real-time tracking for iOS users.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-dumbbell"></i>
              <h3>Health & Fitness Apps</h3>
              <p>Fitness tracking apps with HealthKit integration, workout plans, progress monitoring, and health data sync.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-graduation-cap"></i>
              <h3>Education Apps</h3>
              <p>Learning platforms with courses, quizzes, progress tracking, and interactive content designed for iPad and iPhone.</p>
            </div>
            <div className="app-type-card">
              <i className="fas fa-briefcase"></i>
              <h3>Business Apps</h3>
              <p>Enterprise solutions for productivity, CRM, project management, and team collaboration with iCloud integration.</p>
            </div>
          </div>
        </section>

        <section className="service-cta">
          <h2>Ready to Build Your iOS App?</h2>
          <p>Let's discuss your project and bring your iOS app idea to life</p>
          <div className="cta-buttons">
            <Link to="/contact" className="btn-primary">Get A Free Quote</Link>
            <Link to="/contact" className="btn-secondary">Schedule a Consultation</Link>
          </div>
        </section>
      </div>
    </div>
    </>
  );
};

export default IOSDevelopment;
